import { hashEmailSha256 } from "./seznam-hash.js";
const DEFAULT_RC_WAIT_MS = 4000;
const DEFAULT_RC_POLL_MS = 100;
/**
 * Maps semantic analytics events onto Seznam's rc.js API:
 *   cookieConsentGranted      -> window.rc.retargetingHit
 *   profileCollectionFinished -> window.sznIVA.IS.updateIdentities + window.rc.conversionHit
 *
 * rc.js is loaded asynchronously by <SeznamCounter/>, so every hit waits for it to
 * show up rather than assuming it is already there.
 */
export function makeSeznamTracker(options) {
    const { rtgId, conversionId, hashEmail = hashEmailSha256, rcWaitMs = DEFAULT_RC_WAIT_MS, rcPollMs = DEFAULT_RC_POLL_MS, } = options;
    const getWin = () => options.win ?? (typeof window !== "undefined" ? window : undefined);
    const waitForRc = (method) => new Promise((resolve) => {
        const startedAt = Date.now();
        (function check() {
            const rc = getWin()?.rc;
            if (rc && typeof rc[method] === "function") {
                resolve(rc);
                return;
            }
            if (Date.now() - startedAt >= rcWaitMs) {
                resolve(null);
                return;
            }
            setTimeout(check, rcPollMs);
        })();
    });
    const setEmailIdentity = async (email) => {
        const eid = await hashEmail(email);
        const identityStorage = getWin()?.sznIVA?.IS;
        if (eid && typeof identityStorage?.updateIdentities === "function") {
            identityStorage.updateIdentities({ eid });
        }
    };
    return {
        async track(event) {
            if (!getWin()) {
                return;
            }
            if (event.type === "cookieConsentGranted") {
                if (!event.marketing || rtgId == null) {
                    return;
                }
                const rc = await waitForRc("retargetingHit");
                rc?.retargetingHit({ rtgId, consent: 1 });
                return;
            }
            if (event.type === "profileCollectionFinished") {
                if (conversionId == null) {
                    return;
                }
                // Start polling for rc.js while the e-mail digest is still being computed —
                // the two are independent, and a redirect may be waiting on both.
                const rcReady = waitForRc("conversionHit");
                const email = event.profileData.email;
                if (typeof email === "string" && email) {
                    await setEmailIdentity(email);
                }
                const rc = await rcReady;
                rc?.conversionHit({ id: conversionId, value: null, consent: 1 });
            }
        },
    };
}
